import { useEffect, useState } from "react";
import { Box, Button, Grid, Typography, useMediaQuery } from "@mui/material";
import { theme } from "../../assets/theme";
import { useUserContext } from "../../contexts/user-context";
import {
  getBenchmarks,
  getStats,
  getVerificationDetails,
} from "../../services/insights-service";
import {
  IBenchmarks,
  IconPathLookupType,
  IInsights,
  InsightByStandard,
  IVerifiedScore,
} from "../../types/insights";
import { useStandards } from "../../contexts/StandardsProvider";
import { IconPathType } from "../../types/standard";
import { AppError, Paths } from "../../types/app";
import { useError } from "../../contexts/ErrorContext";
import { StandardsCard } from "../../components/StandardsCard";
import { useNavigate } from "react-router";
import { DashboardInsights } from "../../components/DashboardInsights/DashboardInsights";

type ColorsMapType = {
  [key in IconPathType]: string;
};

const colorsMap: ColorsMapType = {
  transparentPay: "#D3FF53",
  hiring: "#FF8960",
  performanceManagement: "#BAA3FF",
  flexibleWork: "#8AACD4",
  policies: "#E67A94",
  respectWork: "#8DDDFF",
  leadership: "#94D36D",
  sltExec: "#FF9900",
  parentalLeave: "#8EE4DA",
  cultureValues: "#FDE722",
};

export default function Insights() {
  const { handleError } = useError();
  const userContext = useUserContext();
  const { submittedStandards, unsubmittedStandards } = useStandards();
  const navigate = useNavigate();
  const iconPaths: IconPathLookupType = {};
  submittedStandards.forEach((eachStandard) => {
    iconPaths[eachStandard.id] = eachStandard.iconPath;
  });

  const [insights, setInsights] = useState<IInsights | undefined>(undefined);
  const [standardInsights, setStandardInsights] = useState<InsightByStandard[]>(
    [],
  );
  const [benchmarks, setBenchmarks] = useState<IBenchmarks | undefined>(
    undefined,
  );
  const [verifiedScores, setVerifiedScores] = useState<
    IVerifiedScore | undefined
  >(undefined);
  const [iconPathLookup, setIconPathLookup] =
    useState<IconPathLookupType>(iconPaths);
  // const [average, setAverage] = useState<number>(0);
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  // const isIpad = useMediaQuery(theme.breakpoints.down("md"));

  // const [showInfoBanner, setShowInfoBanner] = useState(true);

  // useEffect(() => {
  //   const item = localStorage.getItem("showInfoBanner");
  //   if (item) {
  //     setShowInfoBanner(JSON.parse(item));
  //   }
  //   userContext.set({
  //     selectedStandard: undefined,
  //   });
  //   refreshData();
  // }, [refreshData, userContext]);

  // const hideInformationBanner = () => {
  //   localStorage.setItem("showInfoBanner", JSON.stringify(false));
  //   setShowInfoBanner(false);
  // };

  const getBenchmarkData = async () => {
    const benchmarkData = await getBenchmarks();
    setBenchmarks(benchmarkData);
  };

  const getVerifiedScores = async () => {
    const verifiedScores = await getVerificationDetails();
    setVerifiedScores(verifiedScores);
  };

  const getInsights = async () => {
    try {
      const stats = await getStats();
      setInsights(stats);

      const scores: number[] = [];
      const insightsByStandard: InsightByStandard[] = Object.keys(
        stats.byStandard,
      ).map((eachKey) => {
        const eachStandard = stats.byStandard[eachKey];

        const score = eachStandard.score!;
        scores.push(score);

        return {
          id: parseInt(eachKey),
          name: eachStandard.name,
          color: iconPathLookup
            ? colorsMap[iconPathLookup[parseInt(eachKey)]]
            : "#D3FF53",
          score: score,
          stats: {
            done: eachStandard.done,
            total: eachStandard.done + eachStandard.todo,
          },
        };
      });
      // setInsights(stats);
      // setAverage(
      //   scores.reduce((total, score) => (total += score), 0) /
      //   Object.keys(stats.byStandard).length,
      // );
      setStandardInsights(insightsByStandard);
    } catch (e) {
      const error = e as AppError;
      if (error && error.message) {
        handleError(error.message);
      }
    }
  };
  useEffect(() => {
    const iconPaths: IconPathLookupType = {};
    submittedStandards.forEach((eachStandard) => {
      iconPaths[eachStandard.id] = eachStandard.iconPath;
    });
    setIconPathLookup(iconPaths);
  }, [submittedStandards]);

  useEffect(() => {
    getInsights();
    userContext.set({
      selectedStandard: undefined,
    });
    // eslint-disable-next-line
  }, [iconPathLookup]);

  useEffect(() => {
    getBenchmarkData();
    // eslint-disable-next-line
  }, [insights]);

  useEffect(() => {
    getVerifiedScores();
    // eslint-disable-next-line
  }, [insights]);

  return (
    <Box
      component="main"
      sx={{ flex: 1, m: isMobile ? "15px 10px 30px 10px" : 5 }}
      // bgcolor="#f5f5f5"
    >
      {/* {showInfoBanner ? (
        <Box>
          <Box sx={{ position: "relative" }}>
            <Box
              component="img"
              alt="Information banner"
              src={
                isMobile
                  ? informationBannerMobile
                  : isIpad
                    ? informationBannerIpad
                    : informationBannerDesktop
              }
              sx={{ width: "100%" }}
            />
          </Box>
          <IconButton
            size="medium"
            onClick={hideInformationBanner}
            aria-label="Close Information banner"
            sx={{
              position: "absolute",
              right: isMobile ? "8px" : "30px",
              top: isMobile ? "8px" : "30px",
              border: "1px solid #E5E7EB",
              background: "#fff",
              borderRadius: "8px",
              "&:hover": {
                background: "#fff",
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      ) : null} */}

      <div>
        {unsubmittedStandards.length > 0 ? (
          <div>
            <Typography variant="h2" className="ap-home-page-heading margnT40">
              Complete Your Self Assessments
            </Typography>
            <Grid container>
              {unsubmittedStandards.map((eachStandard, i) => {
                return (
                  <Grid
                    item
                    xs={12}
                    sm={9}
                    md={4}
                    lg={2}
                    xl={2}
                    key={i}
                    sx={{ paddingBottom: 4 }}
                  >
                    <StandardsCard
                      standard={eachStandard}
                      ButtonComponent={
                        <Button
                          onClick={() =>
                            navigate(Paths.QUESTIONNAIRE, {
                              state: { standard: eachStandard },
                            })
                          }
                          role="button"
                          variant="contained"
                          className="standards-btn"
                        >
                          Take the Assessment
                        </Button>
                      }
                    />
                  </Grid>
                );
              })}
            </Grid>
          </div>
        ) : null}
      </div>

      {insights?.total.score && benchmarks && verifiedScores && (
        <DashboardInsights
          standardInsights={standardInsights}
          overallScore={insights.total.score}
          benchmarks={benchmarks}
          verifiedScores={verifiedScores}
        />
      )}
    </Box>
  );
}
